import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { Archive, ArrowRight, Check, LucideIcon, PartyPopper, RotateCcw, X } from 'lucide-react'
import { DateTime } from 'luxon'
import useQueryContext from '../../context/QueryContext'
import {
    TGoalReviewAnswer,
    useGetGoalRecurringTemplateLinks,
    useSaveGoalReviewEntry,
} from '../../services/api/goals.hooks'
import Log from '../../services/api/log'
import { useRecurringTaskTemplates } from '../../services/api/recurring-tasks.hooks'
import { useGetTasksV4, useModifyTask } from '../../services/api/tasks.hooks'
import DetailsViewTemplate from '../templates/DetailsViewTemplate'
import { ThisWeekBlock } from './GoalDetails'
import GoalPlanDialogs from './GoalPlanDialogs'
import { GoalStatusBadge } from './GoalsList'
import { activeRecurringTemplates } from './RecurringGoalHooks'
import { repairOptions, repairPatch, weeksLeft } from './RepairDialog'
import SectionLabel from './SectionLabel'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import {
    GOAL_REVIEW_FIX_LABEL,
    GOAL_REVIEW_READS,
    REVIEW_STEP_DAYS,
    TGoalReview,
    entryForGoal,
    isReviewable,
    lastStepLine,
    nextUnreadGoalId,
    readTakesStep,
    reviewCoversSince,
    reviewDateLabel,
    reviewEntrySummary,
    reviewQueue,
    stepDayDate,
} from './goalReviewRules'
import { linkedTasksForGoal, nextUpOrder } from './goalTasks'
import {
    GOAL_STATUS_LABEL,
    TGoal,
    TGoalPatch,
    TGoalReviewFix,
    TGoalReviewRead,
    formatGoalAmount,
    normalizeGoalEndDate,
} from './goalTypes'

/*
 * The weekly review, drawn in the details pane at /goals/review. It takes GoalDetails' place rather
 * than opening as a GTDialog because Redraft, New task and Archive are already dialogs over this
 * pane, and a modal review would have stacked them. The goal cards in the list are its queue.
 *
 * One rule runs through the whole step: the pane records an answer only after the change it names
 * has actually been saved. Ease back, pause and the rest go through the same goal endpoints they
 * always have, and the review entry is written in their success path — so "Paused" can never be on
 * record for a goal that is still running.
 */

const CHIP_CLASS =
    'inline-flex h-7 items-center rounded-gt-md border border-border bg-card px-2 text-body-sm font-medium text-secondary-foreground'

// 44px targets on touch; GT's own denser rows from the tablet breakpoint up. The four reads sit in one
// segmented well: the picked one lifts onto the background, the rest stay flat in it.
const optionClass = (isPressed: boolean) =>
    cn(
        'flex min-h-11 flex-col justify-center gap-px rounded-[5px] px-3 py-2 text-left',
        'transition-[background-color,box-shadow] duration-fast ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        isPressed ? 'bg-background shadow-gt-m' : 'hover:bg-background/60'
    )
const rowClass = (isPressed: boolean) =>
    cn(
        'flex min-h-11 w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors duration-150',
        'hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
        isPressed && 'bg-muted ring-1 ring-inset ring-foreground'
    )
const STEP_CLASS = cn(
    'flex min-h-11 w-full items-center gap-2.5 rounded-gt-md px-2 text-left text-body-md transition-colors duration-150 md:min-h-9',
    'hover:bg-muted focus-visible:bg-muted focus-visible:outline-none'
)

/** The no-step choice, kept apart from task ids so "nothing picked yet" and "no step" stay different. */
const NO_STEP = 'none'

/** One numbered step per goal in the queue: this one filled, answered ones green, the rest waiting. */
const ReviewDots = ({ total, current, answered }: { total: number; current: number; answered: boolean[] }) => (
    <div className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
            <span
                key={i}
                className={cn(
                    'flex size-4 items-center justify-center rounded-full text-[10px] font-semibold leading-3 tabular-nums transition-colors duration-base ease-out',
                    i === current
                        ? 'bg-foreground text-background'
                        : answered[i]
                        ? 'bg-success/15 text-success'
                        : 'bg-secondary text-muted-foreground'
                )}
            >
                {i + 1}
            </span>
        ))}
    </div>
)

interface TFixRow {
    key: TGoalReviewFix
    icon: LucideIcon
    title: string
    description: string
}

const RETHINK_ROW: TFixRow = {
    key: 'rethink',
    icon: RotateCcw,
    title: 'Rethink the plan',
    description: 'Opens Redraft. Nothing changes until you accept it.',
}
// Archiving discards a goal's progress history (the archive dialog says so before it commits), so
// neither row here promises that history is kept.
const ARCHIVE_ROW: TFixRow = {
    key: 'archive',
    icon: Archive,
    title: 'Archive goal',
    description: 'Leaves your list. Restore it any time.',
}
// Replaces the old "Archive as done": the same achieve the details pane offers, so a goal closed here
// keeps its progress and lands in Accomplished exactly as one closed from its own page does.
const ACHIEVE_ROW: TFixRow = {
    key: 'achieve',
    icon: PartyPopper,
    title: 'Mark achieved',
    description: 'Moves to Accomplished with its progress kept.',
}
const NEW_TARGET_ROW: TFixRow = {
    key: 'new_target',
    icon: ArrowRight,
    title: 'Keep going with a new target',
    description: 'Edit the weekly target or end date.',
}

const FIX_GROUP_LABEL: Partial<Record<TGoalReviewRead, string>> = {
    stuck: 'What would make it possible?',
    not_now: 'Then let it rest',
    done: 'Close it',
}

/** The changes a read may carry for this goal. Repair's own options are reused, so the copy and the
 * patches cannot drift from the Repair dialog's. */
const fixRows = (goal: TGoal, read: TGoalReviewRead): TFixRow[] => {
    const repairs = repairOptions(goal)
    const repair = (key: 'ease' | 'extend' | 'keep' | 'pause'): TFixRow[] =>
        repairs
            .filter((option) => option.key === key)
            .map(({ icon, title, description }) => ({ key, icon, title, description }))
    if (read === 'stuck') return [...repair('ease'), ...repair('extend'), ...repair('keep'), RETHINK_ROW]
    if (read === 'not_now') return [...repair('pause'), ARCHIVE_ROW]
    if (read === 'done') return [ACHIEVE_ROW, NEW_TARGET_ROW]
    return []
}

interface GoalReviewStepProps {
    goal: TGoal
    review: TGoalReview
}

const GoalReviewStep = ({ goal, review }: GoalReviewStepProps) => {
    const { allGoals, taskGoalLinks, updateGoal, setArchiveGoalId, setAchieveGoalId } = useGoalCreation()
    const navigate = useNavigate()
    const { data: tasks } = useGetTasksV4()
    const { data: recurringTemplates } = useRecurringTaskTemplates()
    const { data: recurringTemplateLinks = {} } = useGetGoalRecurringTemplateLinks()
    const { mutate: modifyTask } = useModifyTask()
    const { mutate: saveEntry } = useSaveGoalReviewEntry()
    const { getIdFromOptimisticId } = useQueryContext()

    // Reopening a goal shows the answer already given, so changing your mind starts from it.
    const saved = entryForGoal(review, goal.id)
    const [read, setRead] = useState<TGoalReviewRead | undefined>(saved?.read)
    const [fix, setFix] = useState<TGoalReviewFix | undefined>(saved?.fix)
    const [step, setStep] = useState<string | undefined>(
        saved?.stepTaskId ?? (saved && readTakesStep(saved.read) ? NO_STEP : undefined)
    )
    const [stepDay, setStepDay] = useState<string | undefined>(saved?.stepDay)
    const [targetDraft, setTargetDraft] = useState(goal.weeklyTarget ? formatGoalAmount(goal.weeklyTarget) : '')
    const [endDateDraft, setEndDateDraft] = useState(goal.endDate ?? '')
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [isRedraftOpen, setIsRedraftOpen] = useState(false)
    const [isCommitting, setIsCommitting] = useState(false)
    const [appliedFix, setAppliedFix] = useState<TGoalReviewFix>()
    // Read by the leave-effect below in the same tick an archive lands, before state has re-rendered.
    const isCommittingRef = useRef(false)
    const titleRef = useRef<HTMLHeadingElement>(null)

    // The pane's content just changed under the reader; put focus on what it is now about.
    useEffect(() => titleRef.current?.focus(), [])

    const setCommitting = (next: boolean) => {
        isCommittingRef.current = next
        setIsCommitting(next)
    }

    // A goal that is paused or archived is not walked. Mid-commit it is expected — that is what the
    // answer just did — and the commit moves on by itself; otherwise there is nothing to ask here.
    const walkable = isReviewable(goal)
    useEffect(() => {
        if (!walkable && !isCommittingRef.current) navigate('/goals/review', { replace: true })
    }, [walkable, navigate])

    const linked = useMemo(() => linkedTasksForGoal(goal.id, tasks, taskGoalLinks), [goal.id, tasks, taskGoalLinks])
    const openTasks = useMemo(() => nextUpOrder(linked), [linked])
    const repeatCount = useMemo(
        () =>
            activeRecurringTemplates(recurringTemplates).filter((template) =>
                (recurringTemplateLinks[template.id] ?? []).includes(goal.id)
            ).length,
        [goal.id, recurringTemplateLinks, recurringTemplates]
    )

    // A task made from "Something else…" is picked under its optimistic id and gets its real one a
    // moment later; the answer can only be recorded against the real one.
    const stepTaskId = step && step !== NO_STEP ? getIdFromOptimisticId(step) ?? step : undefined
    const stepTask = stepTaskId ? tasks?.find((task) => task.id === stepTaskId || task.id === step) : undefined
    // A step has to be one of this goal's tasks, under its real id: that is what Next up lists and
    // what the server accepts. A task just made here is neither until its create and its link have
    // both been saved, so the answer waits for them.
    const isStepUnsaved = !!stepTaskId && (!!stepTask?.optimisticId || taskGoalLinks[stepTaskId] !== goal.id)

    const queue = reviewQueue(allGoals, review)
    const position = queue.findIndex((queued) => queued.id === goal.id)
    const hasMore = nextUnreadGoalId(allGoals, review, goal.id) !== null

    const isPaced = !!goal.weeklyTarget && goal.progressMode !== 'manual'
    const left = weeksLeft(goal)
    const offered = read ? fixRows(goal, read) : []
    // A change already made can stop being on offer — a target eased to 1× cannot be eased again —
    // but it is still this goal's answer, so it keeps a row to be shown as chosen on.
    const madeFix = saved?.read === read ? saved?.fix : undefined
    const rows =
        madeFix && !offered.some((row) => row.key === madeFix)
            ? [{ key: madeFix, icon: Check, title: GOAL_REVIEW_FIX_LABEL[madeFix], description: '' }, ...offered]
            : offered
    const takesStep = read === 'moving' || (read === 'stuck' && !!fix)
    // A change that has already been made must not be made again: committing "ease back" twice eases
    // twice. That is true of an answer already on record, and equally of a change that saved in this
    // sitting whose answer then failed to — the retry has to record it, not repeat it.
    const isFixApplied = !!fix && ((saved?.read === read && saved?.fix === fix) || appliedFix === fix)

    const newTargetPatch = (): TGoalPatch | null => {
        const patch: TGoalPatch = {}
        const target = Number(targetDraft)
        if (goal.weeklyTarget && Number.isFinite(target) && target > 0 && target !== goal.weeklyTarget) {
            patch.weeklyTarget = target
        }
        if (endDateDraft !== (goal.endDate ?? '')) patch.endDate = endDateDraft
        // Re-pacing forgives the weeks judged against the old plan, the same as the chips do.
        return Object.keys(patch).length > 0 ? { ...patch, forgive: true } : null
    }

    const isReady =
        !!read &&
        (read === 'moving' || !!fix) &&
        (fix !== 'new_target' || isFixApplied || newTargetPatch() !== null) &&
        !isStepUnsaved

    const mismatch =
        (read === 'stuck' && goal.status === 'on_track' && isPaced) ||
        (read === 'moving' && (goal.status === 'behind' || goal.status === 'off_track'))

    const chooseRead = (next: TGoalReviewRead) => {
        setRead(next)
        // A different read is a different answer: the change and the step chosen for the old one go.
        if (next !== read) {
            setFix(undefined)
            setStep(undefined)
            setStepDay(undefined)
        }
    }

    const chooseFix = (next: TGoalReviewFix) => {
        setFix(next)
        if (next === 'rethink') setIsRedraftOpen(true)
    }

    const chooseStep = (next: string) => {
        setStep(next)
        if (next !== step) setStepDay(undefined)
    }

    const advance = (updated: TGoalReview) => {
        const next = nextUnreadGoalId(allGoals, updated, goal.id)
        if (!next) Log('goal_review_completed')
        navigate(next ? `/goals/review/${next}` : '/goals/review')
    }

    const saveAnswer = (answer: TGoalReviewAnswer) => {
        saveEntry(
            { goalId: goal.id, answer },
            {
                onSuccess: (updated) => {
                    Log(
                        answer.skipped
                            ? 'goal_review_goal_skipped'
                            : {
                                  type: 'goal_review_goal_answered',
                                  read: answer.read,
                                  fix: answer.fix,
                                  hasStep: !!answer.stepTaskId,
                              }
                    )
                    advance(updated)
                },
                onError: () => {
                    setCommitting(false)
                    toast.error('Could not save your answer. Try again.')
                },
            }
        )
    }

    const record = (answer: TGoalReviewAnswer) => {
        setCommitting(true)
        // The step's day is a due date on the task. It is saved before the answer for the same reason
        // the goal change is: the summary must not name a day the task never got.
        if (answer.stepTaskId && answer.stepDay) {
            return modifyTask(
                { id: answer.stepTaskId, dueDate: answer.stepDay },
                {
                    onSuccess: () => saveAnswer(answer),
                    onError: () => setCommitting(false),
                }
            )
        }
        saveAnswer(answer)
    }

    const commit = () => {
        if (!read || !isReady || isCommitting) return
        const answer: TGoalReviewAnswer = {
            read,
            fix,
            stepTaskId: takesStep ? stepTaskId : undefined,
            stepDay: takesStep && stepTaskId ? stepDay : undefined,
        }
        if (!fix || fix === 'rethink' || isFixApplied) return record(answer)
        if (fix === 'achieve') {
            // The achieve dialog owns the confirm and the open-task choice, as archive's does below.
            return setAchieveGoalId(goal.id, {
                onAchieved: () => {
                    setAppliedFix(fix)
                    record(answer)
                },
            })
        }
        if (fix === 'archive' || fix === 'archive_done') {
            // The archive dialog owns the confirm and the choice about linked tasks. Cancelling it
            // changes nothing and records nothing.
            return setArchiveGoalId(goal.id, {
                reason: fix === 'archive_done' ? 'done' : undefined,
                onArchived: () => {
                    setAppliedFix(fix)
                    record(answer)
                },
            })
        }
        const repair = repairOptions(goal).find((option) => option.key === fix)
        const patch = fix === 'new_target' ? newTargetPatch() : repair ? repairPatch(goal, repair) : null
        if (!patch) return
        setCommitting(true)
        updateGoal(goal.id, patch, {
            onSuccess: () => {
                setAppliedFix(fix)
                record(answer)
            },
            onError: () => setCommitting(false),
        })
    }

    const nextLabel = (() => {
        const withoutStep = takesStep && (!step || step === NO_STEP)
        if (hasMore) return withoutStep ? 'Next without a step' : 'Next goal'
        return withoutStep ? 'Finish without a step' : 'Finish review'
    })()

    const stepNote = lastStepLine(goal, review, (taskId) => tasks?.find((task) => task.id === taskId)?.is_done)
    const lastNote = goal.recent.find((entry) => (entry.amount ?? 0) === 0 && !!entry.at)
    // The count on the card is the goal's pace week, which runs Monday to Sunday. On the review day
    // that is the week being looked back on. Opened later in the week it is already the new one, so
    // the card says "so far" instead of claiming to cover a week it is no longer showing.
    const isReviewDay = DateTime.local().toISODate() === review.weekStart
    const evidenceLabel = isReviewDay ? 'Since your last review' : 'This week so far'
    const since = isReviewDay ? reviewCoversSince(review) : ''

    return (
        <DetailsViewTemplate>
            <div className="flex items-center gap-2">
                <SectionLabel>Weekly review</SectionLabel>
                <span className="text-body-sm tabular-nums text-muted-foreground">
                    {position + 1} of {queue.length}
                </span>
                <ReviewDots
                    total={queue.length}
                    current={position}
                    answered={queue.map((queued) => !!entryForGoal(review, queued.id))}
                />
                {/* The mobile app bar's back arrow already leaves the review; a second control beside
                    it would be a duplicate with a different icon. */}
                <button
                    type="button"
                    aria-label="Close review"
                    onClick={() => navigate(`/goals/${goal.id}`)}
                    className="ml-auto hidden size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:flex"
                >
                    <X className="size-4" />
                </button>
            </div>

            <h1 ref={titleRef} tabIndex={-1} className="text-title-lg text-foreground focus:outline-none">
                {goal.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
                <GoalStatusBadge status={goal.status} />
                <span className={CHIP_CLASS}>
                    {left === null ? 'No deadline' : `${left} week${left === 1 ? '' : 's'} left`}
                </span>
                <span className={CHIP_CLASS}>
                    {isPaced
                        ? `${formatGoalAmount(goal.weeklyTarget ?? 0)}${goal.unit === 'hrs' ? ' hrs' : '×'} / week`
                        : goal.progressMode === 'manual'
                        ? 'Self-graded'
                        : goal.targetLabel || 'No weekly pace'}
                </span>
            </div>

            {isPaced ? (
                <ThisWeekBlock
                    goal={goal}
                    hasLinkedTasks={linked.length > 0}
                    isArchived={false}
                    repeatCount={repeatCount}
                    recurringCreditGoalCount={0}
                    review={{ label: evidenceLabel, since, notes: stepNote ? [stepNote] : [] }}
                />
            ) : (
                <div className="flex flex-col gap-2.5 rounded-gt-md border border-border bg-card p-3">
                    <div className="flex items-baseline gap-2">
                        <SectionLabel>{evidenceLabel}</SectionLabel>
                        <span className="ml-auto text-body-sm text-muted-foreground">{since}</span>
                    </div>
                    <p className="text-body-sm text-muted-foreground">
                        {goal.progressMode === 'manual'
                            ? 'Self-graded, so nothing is counted.'
                            : `${goal.progressLabel}. No weekly pace is set, so there is no count.`}
                        {lastNote?.at &&
                            ` Your last note, ${DateTime.fromISO(lastNote.at).toFormat('LLL d')}: “${lastNote.title}”`}
                    </p>
                    {stepNote && <p className="text-body-sm text-muted-foreground">{stepNote}</p>}
                </div>
            )}

            <p id="goal-review-question" className="mt-1.5 text-title-md text-foreground">
                How is this going, honestly?
            </p>
            <div
                className="grid grid-cols-1 gap-0.5 rounded-gt-md bg-secondary p-0.5 md:grid-cols-2"
                role="group"
                aria-labelledby="goal-review-question"
            >
                {GOAL_REVIEW_READS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={read === option.value}
                        onClick={() => chooseRead(option.value)}
                        className={optionClass(read === option.value)}
                    >
                        <span className="text-body-md font-medium text-foreground">{option.label}</span>
                        <span className="text-body-sm text-muted-foreground">{option.description}</span>
                    </button>
                ))}
            </div>
            {mismatch && (
                <p className="border-l-2 border-border pl-3 text-body-sm text-secondary-foreground">
                    The count says {GOAL_STATUS_LABEL[goal.status].toLowerCase()}. Your read is saved beside it, not
                    over it.
                </p>
            )}

            {read && rows.length > 0 && (
                <>
                    <SectionLabel>
                        <span id="goal-review-fix">{FIX_GROUP_LABEL[read]}</span>
                    </SectionLabel>
                    <div className="flex flex-col gap-0.5" role="group" aria-labelledby="goal-review-fix">
                        {rows.map((row) => (
                            <button
                                key={row.key}
                                type="button"
                                aria-pressed={fix === row.key}
                                onClick={() => chooseFix(row.key)}
                                className={rowClass(fix === row.key)}
                            >
                                <span className="mt-px flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                    <row.icon className="size-4" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-body-sm font-medium text-foreground">{row.title}</span>
                                    <span className="block text-body-sm text-muted-foreground">
                                        {madeFix === row.key
                                            ? `${GOAL_REVIEW_FIX_LABEL[row.key]} in this review.`
                                            : row.description}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </>
            )}
            {fix === 'new_target' && !isFixApplied && (
                <div className="flex flex-wrap items-end gap-3 pl-2">
                    {!!goal.weeklyTarget && (
                        <label className="flex flex-col gap-1 text-label-md text-muted-foreground">
                            Weekly target{goal.unit === 'hrs' ? ' (hrs)' : ''}
                            <Input
                                type="number"
                                min={goal.unit === 'hrs' ? 0.5 : 1}
                                step={goal.unit === 'hrs' ? 0.5 : 1}
                                value={targetDraft}
                                onChange={(e) => setTargetDraft(e.target.value)}
                                className="h-8 w-24"
                            />
                        </label>
                    )}
                    <label className="flex flex-col gap-1 text-label-md text-muted-foreground">
                        End date
                        <Input
                            type="date"
                            value={endDateDraft}
                            onChange={(e) => setEndDateDraft(normalizeGoalEndDate(e.target.value))}
                            className="h-8 w-40"
                        />
                    </label>
                </div>
            )}

            {takesStep && (
                <>
                    <SectionLabel>
                        <span id="goal-review-step">One step for this week</span>
                    </SectionLabel>
                    <div className="flex flex-col gap-0.5" role="group" aria-labelledby="goal-review-step">
                        {openTasks.map((task) => {
                            const isPicked = task.id === stepTaskId || task.id === step
                            return (
                                <button
                                    key={task.id}
                                    type="button"
                                    aria-pressed={isPicked}
                                    onClick={() => chooseStep(task.id)}
                                    className={STEP_CLASS}
                                >
                                    <StepMark isPicked={isPicked} />
                                    <span className="min-w-0 flex-1 truncate">{task.title}</span>
                                    {isPicked && stepDay && (
                                        <span className="shrink-0 text-body-sm text-muted-foreground">
                                            {DateTime.fromISO(stepDay).toFormat('ccc')}
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                        <button type="button" onClick={() => setIsCreateOpen(true)} className={STEP_CLASS}>
                            <StepMark isPicked={false} />
                            <span className="min-w-0 flex-1">Something else…</span>
                            <span className="shrink-0 text-body-sm text-muted-foreground">opens New task</span>
                        </button>
                        <button
                            type="button"
                            aria-pressed={step === NO_STEP}
                            onClick={() => chooseStep(NO_STEP)}
                            className={STEP_CLASS}
                        >
                            <StepMark isPicked={step === NO_STEP} />
                            <span className="min-w-0 flex-1">No step this week</span>
                        </button>
                    </div>
                    {stepTaskId && (
                        <div className="flex flex-wrap gap-1 pl-8" role="group" aria-label="Day for this step">
                            {REVIEW_STEP_DAYS.map((label, i) => {
                                const date = stepDayDate(i + 1, DateTime.local())
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        aria-pressed={stepDay === date}
                                        onClick={() => setStepDay(stepDay === date ? undefined : date)}
                                        className={cn(
                                            'h-11 min-w-10 rounded-md border border-border bg-background px-2 text-label-md transition-colors md:h-7',
                                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                            stepDay === date && 'border-foreground bg-foreground text-background'
                                        )}
                                    >
                                        {label}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </>
            )}

            {/* In flow rather than sticky: a sticky bar would sit under the floating capture button on
                a phone. The spacer below is what lets the last button scroll clear of it. */}
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-hairline pt-3">
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 text-muted-foreground md:h-8"
                    disabled={isCommitting}
                    onClick={() => record({ skipped: true })}
                >
                    Skip this goal
                </Button>
                <Button
                    size="sm"
                    className="h-10 bg-action px-4 text-action-foreground hover:bg-action-hover active:scale-[0.96] md:h-8 md:px-3"
                    disabled={!isReady || isCommitting}
                    onClick={commit}
                >
                    {nextLabel}
                </Button>
            </div>
            <div className="h-[88px] shrink-0 md:hidden" aria-hidden="true" />

            <GoalPlanDialogs
                goal={goal}
                isCreateOpen={isCreateOpen}
                onCreateOpenChange={setIsCreateOpen}
                isRedraftOpen={isRedraftOpen}
                onRedraftOpenChange={setIsRedraftOpen}
                onTaskCreated={chooseStep}
                onGoalLinkFailed={() => {
                    // The task exists but is not this goal's, so it cannot be the goal's step.
                    setStep(undefined)
                    toast.error('Task created, but not linked to this goal. Pick a step again.')
                }}
            />
        </DetailsViewTemplate>
    )
}

const StepMark = ({ isPicked }: { isPicked: boolean }) => (
    <span
        aria-hidden="true"
        className={cn(
            'size-3.5 shrink-0 rounded-full border-[1.5px] border-muted-foreground/60',
            isPicked && 'border-4 border-foreground'
        )}
    />
)

/** The closing summary: every answer on one screen, and when the next review opens. */
const GoalReviewSummary = ({ review }: { review: TGoalReview }) => {
    const { allGoals } = useGoalCreation()
    const navigate = useNavigate()
    const titleRef = useRef<HTMLHeadingElement>(null)
    useEffect(() => titleRef.current?.focus(), [])

    const queue = reviewQueue(allGoals, review)
    const firstWalkable = queue.find(isReviewable)
    const reviewedOn = review.entries.reduce((latest, entry) => (entry.at > latest ? entry.at : latest), '')

    return (
        <DetailsViewTemplate>
            <div className="flex items-center gap-2">
                <SectionLabel>Weekly review</SectionLabel>
                {reviewedOn && (
                    <span className="text-body-sm text-muted-foreground">{reviewDateLabel(reviewedOn)}</span>
                )}
            </div>
            <h1 ref={titleRef} tabIndex={-1} className="text-title-lg text-foreground focus:outline-none">
                Here’s where you left it.
            </h1>
            <p className="text-body-sm text-muted-foreground">
                Nothing here is scored. Steps are in your task list with their days.
            </p>
            <ul className="flex flex-col" data-testid="goal-review-summary">
                {queue.map((goal) => {
                    const entry = entryForGoal(review, goal.id)
                    return (
                        <li key={goal.id} className="flex items-baseline gap-3 border-b border-hairline py-2">
                            <span className="min-w-0 flex-1 text-body-md font-medium text-foreground">
                                {goal.title}
                            </span>
                            <span className="text-right text-body-sm text-muted-foreground">
                                {entry ? reviewEntrySummary(entry) : ''}
                            </span>
                        </li>
                    )
                })}
            </ul>
            <div className="flex items-center gap-2">
                <SectionLabel>Next review</SectionLabel>
                <span className="text-body-sm text-muted-foreground">{reviewDateLabel(review.nextReview)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-hairline pt-3">
                {/* Running it again reopens each goal on the answer it already has; changes already
                    made stay made. */}
                {firstWalkable ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 text-muted-foreground md:h-8"
                        onClick={() => navigate(`/goals/review/${firstWalkable.id}`)}
                    >
                        Run it again
                    </Button>
                ) : (
                    <span />
                )}
                <Button
                    size="sm"
                    className="h-10 bg-action px-4 text-action-foreground hover:bg-action-hover active:scale-[0.96] md:h-8 md:px-3"
                    onClick={() => navigate('/goals')}
                >
                    Done
                </Button>
            </div>
            <div className="h-[88px] shrink-0 md:hidden" aria-hidden="true" />
        </DetailsViewTemplate>
    )
}

interface GoalReviewPaneProps {
    /** The goal being read, or null for the closing summary. */
    goal: TGoal | null
    review: TGoalReview
}
const GoalReviewPane = ({ goal, review }: GoalReviewPaneProps) =>
    // Keyed by goal so each one starts from its own saved answer, never the last goal's choices.
    goal ? <GoalReviewStep key={goal.id} goal={goal} review={review} /> : <GoalReviewSummary review={review} />

export default GoalReviewPane
